import React from "react";
import {
  getInsuranceTypeFamily,
  insuranceTypeFamilyOrder,
  type InsuranceTypeFamily,
} from "@/lib/data/insurance";
import type { InsuranceTypeCount } from "@/lib/fetch/insurance";
import { INSURANCE_FAMILY_ICONS } from "./insurance-type-meta";

export type InsuranceFamilyCounts = Record<InsuranceTypeFamily, number>;

/** Products per family from a provider's per-type counts, zero-filled. */
export const getInsuranceFamilyCounts = (
  types: readonly InsuranceTypeCount[],
): InsuranceFamilyCounts => {
  const counts = Object.fromEntries(
    insuranceTypeFamilyOrder.map((family) => [family, 0]),
  ) as InsuranceFamilyCounts;

  for (const entry of types) {
    counts[getInsuranceTypeFamily(entry.type)] += entry.count;
  }

  return counts;
};

/** Families with products, largest first. */
export const getCoveredInsuranceFamilies = (counts: InsuranceFamilyCounts) =>
  insuranceTypeFamilyOrder
    .filter((family) => counts[family] > 0)
    .sort((left, right) => counts[right] - counts[left]);

type Props = {
  counts: InsuranceFamilyCounts;
  labels: Record<InsuranceTypeFamily, string>;
  size?: "sm" | "lg";
};

const SIZES = {
  sm: { tile: "size-8 rounded-lg", icon: "size-4", badge: "hidden" },
  lg: {
    tile: "size-11 rounded-xl sm:size-12",
    icon: "size-5",
    badge:
      "absolute -right-1.5 -top-1.5 min-w-5 rounded-full border border-border bg-background px-1 text-center text-[10px] font-semibold tabular-nums leading-4 text-foreground",
  },
} as const;

/**
 * One tile per insurance family: lime when the provider has products in it,
 * muted when it does not. Reads as a coverage map at a glance, which is the
 * question a provider tile or header has to answer before anything else.
 */
const InsuranceFamilyStrip: React.FC<Props> = ({
  counts,
  labels,
  size = "sm",
}) => {
  const style = SIZES[size];

  return (
    <ul className="flex flex-wrap gap-1.5">
      {insuranceTypeFamilyOrder.map((family) => {
        const Icon = INSURANCE_FAMILY_ICONS[family];
        const count = counts[family];
        const covered = count > 0;

        return (
          <li
            key={family}
            title={`${labels[family]}: ${count}`}
            className={`relative flex flex-none items-center justify-center ${style.tile} ${
              covered
                ? "bg-secondary text-secondary-foreground"
                : "bg-muted text-muted-foreground/40"
            }`}
          >
            <Icon className={style.icon} aria-hidden />
            <span className="sr-only">
              {labels[family]}: {count}
            </span>
            {covered && <span className={style.badge}>{count}</span>}
          </li>
        );
      })}
    </ul>
  );
};

export default InsuranceFamilyStrip;
